import { Check } from "lucide-react";
import { TRANSPORT_OPTIONS, TRANSPORT_LABEL, prettyDate } from "@/lib/addons";

/**
 * Guest / attendee-side picker: shown on signup / RSVP forms when the
 * activity has add-ons offered. Values default to no selection.
 *
 * Props:
 *  - activity: { offer_transport, transport_options, offer_accommodation, accommodation_dates }
 *  - transportChoice: string | null
 *  - onTransportChoice(value | null)
 *  - accommodationDates: string[]
 *  - onAccommodationDates(list)
 *  - idPrefix: for data-testid stability
 */
export default function AddonsSelector({
    activity,
    transportChoice,
    onTransportChoice,
    accommodationDates,
    onAccommodationDates,
    idPrefix = "rsvp",
}) {
    if (!activity) return null;
    const showTransport = activity.offer_transport && (activity.transport_options || []).length > 0;
    const showAccommodation = activity.offer_accommodation && (activity.accommodation_dates || []).length > 0;
    if (!showTransport && !showAccommodation) return null;

    const toggleDate = (d) => {
        const set = new Set(accommodationDates || []);
        if (set.has(d)) set.delete(d); else set.add(d);
        onAccommodationDates(Array.from(set).sort());
    };

    return (
        <div className="space-y-4" data-testid={`${idPrefix}-addons`}>
            {showTransport && (
                <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium mb-2">
                        Transportation
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <ChoiceCard
                            selected={!transportChoice}
                            onClick={() => onTransportChoice(null)}
                            label="I don't need transport"
                            muted
                            testId={`${idPrefix}-transport-none`}
                        />
                        {activity.transport_options.map(opt => (
                            <ChoiceCard
                                key={opt}
                                selected={transportChoice === opt}
                                onClick={() => onTransportChoice(opt)}
                                label={TRANSPORT_LABEL[opt] || opt}
                                testId={`${idPrefix}-transport-${opt}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {showAccommodation && (
                <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium mb-2">
                        Accommodation nights
                    </p>
                    <p className="text-xs text-[#888885] mb-2">Select any nights you need. Leave empty if you don&apos;t need accommodation.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {activity.accommodation_dates.map(d => {
                            const active = (accommodationDates || []).includes(d);
                            return (
                                <ChoiceCard
                                    key={d}
                                    selected={active}
                                    onClick={() => toggleDate(d)}
                                    label={prettyDate(d)}
                                    subLabel={d}
                                    testId={`${idPrefix}-acc-${d}`}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function ChoiceCard({ selected, onClick, label, subLabel, muted, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={selected}
            className={`text-left px-3 py-2.5 border transition-colors flex items-center justify-between gap-2 ${
                selected
                    ? "border-[#111110] bg-[#F0F0EE]"
                    : "border-[#E5E5E3] bg-white hover:border-[#888885]"
            }`}
        >
            <span className="flex flex-col min-w-0">
                <span className={`text-sm truncate ${muted && !selected ? "text-[#666664]" : "text-[#111110]"}`}>
                    {label}
                </span>
                {subLabel && <span className="text-[10px] text-[#888885] font-mono tracking-wide">{subLabel}</span>}
            </span>
            {selected && <Check className="w-4 h-4 text-[#111110] shrink-0" strokeWidth={2} />}
        </button>
    );
}
